import React from 'react';
import { useSelector } from 'react-redux';
import PresignedImage from '../common/PresignedImage';
import FileTypeIcon from '../common/FileTypeIcon';
import TimelineFileCard from './TimelineFileCard';
import AttachmentLikeHeart from './AttachmentLikeHeart';
import {
  formatFileSize,
  getFileExtensionLabel,
  getFileIconKind,
  isDisplayableImage,
  isLinkAttachment,
  canPreviewInApp,
} from '../../utils/fileUtils';

const SHOW_LIMIT = 4;

const previewGallery = (list = []) => list.filter((item) => canPreviewInApp(item));

const openAttachment = (attachment, list, { onViewImage, onDownload }) => {
  if (isLinkAttachment(attachment) && attachment.fileUrl) {
    window.open(attachment.fileUrl.replace(/&amp;/g, '&'), '_blank', 'noopener,noreferrer');
    return;
  }
  if (canPreviewInApp(attachment)) {
    const gallery = previewGallery(list);
    const imageIndex = gallery.findIndex(
      (item) =>
        (attachment.id && item.id && item.id === attachment.id) ||
        item.fileUrl === attachment.fileUrl
    );
    onViewImage?.(gallery, imageIndex >= 0 ? imageIndex : 0);
    return;
  }
  onDownload?.(attachment);
};

const getGridConfig = (count, hasMore) => {
  const base = 'gap-0.5 max-w-[300px] w-full';

  if (hasMore || count === 4) {
    return {
      className: `grid grid-cols-2 grid-rows-2 ${base} h-[220px]`,
      tileHeight: 'h-full min-h-0',
      cells: [
        { attachmentIndex: 0, className: '' },
        { attachmentIndex: 1, className: '' },
        { attachmentIndex: 2, className: '' },
        { isOverflow: true, className: '' },
      ],
    };
  }
  if (count === 3) {
    return {
      className: `grid grid-cols-2 grid-rows-2 ${base} h-[220px]`,
      tileHeight: 'h-full min-h-0',
      cells: [
        { attachmentIndex: 0, className: 'row-span-2' },
        { attachmentIndex: 1, className: '' },
        { attachmentIndex: 2, className: '' },
      ],
    };
  }
  if (count === 2) {
    return {
      className: `grid grid-cols-2 ${base} h-[160px]`,
      tileHeight: 'h-full min-h-0',
      cells: [
        { attachmentIndex: 0, className: '' },
        { attachmentIndex: 1, className: '' },
      ],
    };
  }
  return {
    className: base,
    tileHeight: 'h-52 sm:h-56 w-full',
    cells: [{ attachmentIndex: 0, className: '' }],
  };
};

const AttachmentTile = ({
  attachment,
  index,
  allAttachments,
  onViewImage,
  onDownload,
  onViewAll,
  isOverflow,
  moreCount,
  overflowBackground,
  className = '',
  tileHeight = 'h-28',
  onRemove,
}) => {
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOverflow) {
      const imageAttachments = allAttachments.filter(isDisplayableImage);
      const allAreImages =
        imageAttachments.length > 0 &&
        imageAttachments.length === allAttachments.length;
      if (allAreImages) {
        onViewImage?.(imageAttachments, 3);
      } else {
        onViewAll?.(allAttachments);
      }
      return;
    }
    openAttachment(attachment, allAttachments, { onViewImage, onDownload });
  };

  const tileBase =
    `relative w-full ${tileHeight} overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer hover:opacity-95 transition-opacity ${className}`;

  if (isOverflow) {
    return (
      <button type="button" className={tileBase} onClick={handleClick} title={`${moreCount} more attachments`}>
        {overflowBackground && isDisplayableImage(overflowBackground) && (
          <PresignedImage
            fileKey={overflowBackground.fileUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loadingClassName="absolute inset-0 w-full h-full bg-gray-300 dark:bg-gray-600 animate-pulse"
          />
        )}
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
          <span className="text-white text-xl font-bold">+{moreCount}</span>
        </div>
        {onRemove && (
          <RemoveButton onRemove={() => onRemove(index)} />
        )}
      </button>
    );
  }

  if (isDisplayableImage(attachment)) {
    return (
      <button
        type="button"
        className={tileBase}
        onClick={handleClick}
        title={attachment.fileName}
        aria-label={`View ${attachment.fileName}`}
      >
        <PresignedImage
          fileKey={attachment.fileUrl}
          alt={attachment.fileName}
          className="w-full h-full object-cover pointer-events-none"
          loadingClassName="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse"
        />
        <AttachmentLikeHeart
          attachment={attachment}
          userId={currentUserId}
          size="sm"
          className="absolute bottom-1.5 right-1.5 z-[2]"
        />
        {onRemove && <RemoveButton onRemove={() => onRemove(index)} />}
      </button>
    );
  }

  const ext = getFileExtensionLabel(attachment.fileName);
  const iconKind =
    attachment.fileType === 'AUDIO'
      ? 'audio'
      : getFileIconKind(ext.toLowerCase());

  return (
    <button
      type="button"
      className={`${tileBase} flex flex-col items-center justify-center p-2 text-gray-600 dark:text-gray-300`}
      onClick={handleClick}
      title={attachment.fileName}
    >
      <FileTypeIcon
        kind={iconKind}
        attachment={attachment}
        extension={ext}
        size="md"
        className="mb-1"
      />
      <p className="text-[10px] font-medium truncate w-full text-center px-1">{attachment.fileName}</p>
      {attachment.fileSize ? (
        <p className="text-[9px] opacity-70">{formatFileSize(attachment.fileSize)}</p>
      ) : ext ? (
        <p className="text-[9px] opacity-70">{ext}</p>
      ) : null}
      {onRemove && <RemoveButton onRemove={() => onRemove(index)} />}
    </button>
  );
};

const RemoveButton = ({ onRemove }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onRemove();
    }}
    className="absolute top-1 right-1 z-10 p-0.5 rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors"
    title="Remove"
  >
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
);

const AttachmentGrid = ({
  attachments = [],
  onViewImage,
  onDownload,
  onViewAll,
  onRemove,
  className = '',
}) => {
  if (!attachments.length) return null;

  const imageAttachments = attachments.filter(isDisplayableImage);
  const fileAttachments = attachments.filter((a) => !isDisplayableImage(a));
  const onlyFiles = imageAttachments.length === 0 && fileAttachments.length > 0;

  if (onlyFiles) {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        {fileAttachments.map((attachment, index) => (
          <TimelineFileCard
            key={attachment.id || attachment.fileUrl || index}
            attachment={attachment}
            onClick={(att) => openAttachment(att, attachments, { onViewImage, onDownload })}
            onRemove={onRemove ? () => onRemove(attachments.indexOf(attachment)) : undefined}
          />
        ))}
      </div>
    );
  }

  const hasMore = imageAttachments.length > SHOW_LIMIT;
  const moreCount = imageAttachments.length - 3;
  const displayCount = hasMore ? 3 : Math.min(imageAttachments.length, SHOW_LIMIT);
  const { className: gridClassName, tileHeight, cells } = getGridConfig(displayCount, hasMore);

  return (
    <div className={`space-y-2 ${className}`}>
      {imageAttachments.length > 0 && (
        <div className="rounded-lg overflow-hidden">
          <div className={gridClassName}>
            {cells.map((cell) => {
              if (cell.isOverflow) {
                return (
                  <AttachmentTile
                    key="overflow"
                    index={3}
                    allAttachments={attachments}
                    onViewImage={onViewImage}
                    onDownload={onDownload}
                    onViewAll={onViewAll}
                    isOverflow
                    moreCount={moreCount}
                    overflowBackground={imageAttachments[3]}
                    className={cell.className}
                    tileHeight={tileHeight}
                  />
                );
              }
              const attachment = imageAttachments[cell.attachmentIndex];
              if (!attachment) return null;
              return (
                <AttachmentTile
                  key={attachment.id || cell.attachmentIndex}
                  attachment={attachment}
                  index={attachments.indexOf(attachment)}
                  allAttachments={attachments}
                  onViewImage={onViewImage}
                  onDownload={onDownload}
                  onViewAll={onViewAll}
                  className={cell.className}
                  tileHeight={tileHeight}
                  onRemove={onRemove}
                />
              );
            })}
          </div>
        </div>
      )}

      {fileAttachments.length > 0 && (
        <div className="flex flex-col gap-2">
          {fileAttachments.map((attachment, index) => (
            <TimelineFileCard
              key={attachment.id || attachment.fileUrl || `file-${index}`}
              attachment={attachment}
              onClick={(att) => openAttachment(att, attachments, { onViewImage, onDownload })}
              onRemove={onRemove ? () => onRemove(attachments.indexOf(attachment)) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttachmentGrid;
